import {
  Entities,
  mergeEntities,
  mergeEntity,
  ListReducer,
  ObjectReducer,
  Path
} from '../store';
import {
  NavigationUserInfo,
  NavigationProjectFeedItem,
  NavigationProjectFeedApiResponse,
  NavigationProjectFeedItemApiResponse,
} from './navigation.model';
import { transformUserBalances } from '../user/user.reducer';
import { UserApi } from '../api-typings/users';
import { WebsocketProjectData } from '../websocket';

export const navigationUserInfoReducer: ObjectReducer<'navigationUserInfo'> = (state = {}, action) => {
  switch (action.type) {
    case 'API_FETCH_SUCCESS':
      if (action.payload && action.payload.ref.path[0] === 'navigationUserInfo') {
        const { data, ref } = action.payload;
        return mergeEntity(state, transformNavigationUserInfo(data), ref.path);
      }
      return state;
    default:
      return state;
  }
};

export const navigationNotificationPreferencesReducer: ObjectReducer<'navigationNotificationPreferences'> = (state = {}, action) => {
  return state;
};

export const navigationProjectFeedReducer: ListReducer<'navigationProjectFeed'> = (state = {}, action) => {
  switch (action.type) {
    case 'API_FETCH_SUCCESS':
      if (action.payload && action.payload.ref.path[0] === 'navigationProjectFeed') {
        const ref = action.payload.ref;
        const data = <NavigationProjectFeedApiResponse>action.payload.data;
        return mergeEntities<'navigationProjectFeed'>(
          state,
          transformNavigationProjectFeedItems(data),
          ref,
        );
      }
      return state;
    case 'WS_MESSAGE':
      if (action.payload.type === 'PROJECT') {
        const { data, userId } = action.payload;
        return mergeEntities<'navigationProjectFeed'>(
          state,
          transformNavigationProjectFeedWS(data),
          { path: ['navigationProjectFeed', userId] },
          'time'
        );
      }
      return state;
    default:
      return state;
  }
};

export const navigationProjectFeedFailingProjectsReducer: ListReducer<'navigationProjectFeedFailingProjects'> = (state = {}, action) => {
  switch (action.type) {
    case 'API_FETCH_SUCCESS':
      if (action.payload && action.payload.ref.path[0] === 'navigationProjectFeedFailingProjects') {
        const ref = action.payload.ref;
        const data = <NavigationProjectFeedApiResponse>action.payload.data;
        return mergeEntities<'navigationProjectFeedFailingProjects'>(
          state,
          transformNavigationProjectFeedItems(data),
          ref,
        );
      }
      return state;
    default:
      return state;
  }
};

export const navigationProjectFeedFailingContestsReducer: ListReducer<'navigationProjectFeedFailingContests'> = (state = {}, action) => {
  switch (action.type) {
    case 'API_FETCH_SUCCESS':
      if (action.payload && action.payload.ref.path[0] === 'navigationProjectFeedFailingContests') {
        const ref = action.payload.ref;
        const data = <NavigationProjectFeedApiResponse>action.payload.data;
        return mergeEntities<'navigationProjectFeedFailingContests'>(
          state,
          transformNavigationProjectFeedItems(data),
          ref,
        );
      }
      return state;
    default:
      return state;
  }
};

export const navigationUserRecentProjectsAndContestsReducer:
  ListReducer<'navigationUserRecentProjectsAndContests'> = (state = {}, action) => {
    switch (action.type) {
      case 'API_FETCH_SUCCESS':
        if (action.payload && action.payload.ref.path[0] === 'navigationUserRecentProjectsAndContests') {
          const ref = action.payload.ref;
          const data = <any>action.payload.data;
          return mergeEntities<'navigationUserRecentProjectsAndContests'>(
            state,
            transformUserRecentProjectsAndContests(data),
            ref,
          );
        }
        return state;
      default:
        return state;
    }
  };

export const navigationUserBalancesReducer: ListReducer<'navigationUserBalances'> = (state = {}, action) => {
  switch (action.type) {
    case 'API_FETCH_SUCCESS':
      if (action.payload && action.payload.ref.path[0] === 'navigationUserBalances') {
        const ref = action.payload.ref;
        const data = <UserApi>action.payload.data;
        return mergeEntities<'navigationUserBalances'>(
          state,
          transformUserBalances(data),
          ref,
        );
      }
      return state;
    default:
      return state;
  }
};

// This takes an API response and transforms it into a User object
function transformNavigationUserInfo(navigation: any): NavigationUserInfo {
  return {
    id: navigation.id,
    username: navigation.username,
    publicName: navigation.publicName,
    isFreeTrialEligible: navigation.isFreeTrialEligible,
    isPlusTrialEligible: navigation.isPlusTrialEligible,
    trialPackageId: navigation.trialPackageId,
    isPremium: navigation.isPremium,
    logo: navigation.logo,
    membership: navigation.membership,
    hideMembershipUpgradeButton: navigation.hideMembershipUpgradeButton,
    trialDuration: navigation.trialDuration,
    trialLandingUrl: navigation.trialLandingUrl,
    monthlyTrial: navigation.monthlyTrial,
    canSwitchToUsers: navigation.canSwitchToUsers,
    isNoCommissionEligibleReferrer: navigation.isNoCommissionEligibleReferrer,
  }
}

// This takes an API response and transforms it into a NavigationProjectFeed object
function transformNavigationProjectFeedItem(
  item: NavigationProjectFeedItemApiResponse,
): Entities<NavigationProjectFeedItem> {
  return {
    [`${item.type}-${item.id}`]: {
      id: item.id,
      actionText: item.actionText,
      appendedDescr: item.appended_descr,
      currency: item.currency,
      currencyCode: item.currencyCode,
      exchangerate: item.exchangerate,
      featured: item.featured,
      freeBidUntil: item.free_bid_until,
      fulltime: item.fulltime,
      // hidebids: item.hidebids,
      imgUrl: item.imgUrl,
      isMinimallyPostedProject: item.isMinimallyPostedProject,
      jobs: item.jobs.map(job => Number(job)),
      jobString: item.jobString,
      linkUrl: item.linkUrl,
      maxbudget: Number(item.maxbudget),
      minbudget: Number(item.minbudget),
      minimallyPostedProjectBudgetSet: item.minimallyPostedProjectBudgetSet,
      nonpublic: item.nonpublic,
      projIsHourly: item.projIsHourly,
      showBudgetMinimallyPostedProject: item.showBudgetMinimallyPostedProject,
      submitDate: item.submitDate,
      text: item.text,
      time: item.time * 1000, // Turns seconds to miliseconds
      title: item.title,
      type: item.type,
      userId: Number(item.userId),
      userName: item.userName,
    },
  };
}

// This takes an API response and transforms it into a NavigationProjectFeed object
function transformNavigationProjectFeedItems(
  items: NavigationProjectFeedApiResponse,
): Entities<NavigationProjectFeedItem> {
  if (items) {
    return items.reduce(
      (acc, item) => ({
        ...acc,
        ...transformNavigationProjectFeedItem(item),
      }),
      {},
    );
  }
  return {};
}

function transformNavigationProjectFeedWS(
  item: WebsocketProjectData,
): Entities<NavigationProjectFeedItem> {
  return {
    [`project-${item.id}`]: {
      id: item.id,
      actionText: item.actionText,
      appendedDescr: item.appended_descr,
      currency: item.currency,
      currencyCode: item.currencyCode,
      exchangerate: item.exchangerate,
      featured: item.featured,
      freeBidUntil: item.free_bid_until,
      fulltime: item.fulltime,
      imgUrl: item.imgUrl,
      isMinimallyPostedProject: item.isMinimallyPostedProject,
      jobs: item.jobs.map(job => Number(job)),
      jobString: item.jobString,
      linkUrl: item.linkUrl,
      maxbudget: Number(item.maxbudget),
      minbudget: Number(item.minbudget),
      minimallyPostedProjectBudgetSet: item.minimallyPostedProjectBudgetSet,
      nonpublic: item.nonpublic,
      projIsHourly: item.projIsHourly,
      showBudgetMinimallyPostedProject: item.showBudgetMinimallyPostedProject,
      submitDate: item.submitDate,
      text: item.text,
      time: item.time * 1000, // Turns seconds to miliseconds
      title: item.title,
      type: 'project',
      userId: Number(item.userId),
      userName: item.userName,
    },
  };
}

function transformUserRecentProjectsAndContests(item: any) {
  return item.list.map((listItem: any) => ({
    ...listItem,
    urlPart: listItem.type === 'project'
      ? `/projects/${listItem.urlPart}`
      : listItem.urlPart,
  }));
}



// WEBPACK FOOTER //
// ./src/app/@shared/datastore/navigation/navigation.reducer.ts